import type { ComprobanteInput, CorregirComprobanteInput, ReintegroInput, ResumenRetencionesDTO, RetencionFilaDTO, RetencionPosteriorInput, VersionRetencionDTO } from '@mg/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { get, post, put } from '@/lib/api.ts';

export interface FiltrosRetenciones {
  bandeja?: string | undefined;
  tipo?: string | undefined;
  q?: string | undefined;
  pagina?: number | undefined;
  limite?: number | undefined;
}
export interface PaginaRetenciones {
  datos: RetencionFilaDTO[];
  total: number;
  pagina: number;
  limite: number;
}

export const clavesRetenciones = {
  lista: (f: FiltrosRetenciones) => ['retenciones', 'lista', f] as const,
  resumen: ['retenciones', 'resumen'] as const,
  reporte: ['retenciones', 'reporte'] as const,
  versiones: (id: string) => ['retenciones', 'versiones', id] as const,
};

export const useRetenciones = (f: FiltrosRetenciones) => useQuery({ queryKey: clavesRetenciones.lista(f), queryFn: () => get<PaginaRetenciones>('/retenciones', f as Record<string, string | number | undefined>), placeholderData: (p) => p });
export const useResumenRetenciones = () => useQuery({ queryKey: clavesRetenciones.resumen, queryFn: () => get<ResumenRetencionesDTO>('/retenciones/resumen') });
export const useReporteReintegros = (habilitado: boolean) => useQuery({ queryKey: clavesRetenciones.reporte, queryFn: () => get<{ datos: RetencionFilaDTO[]; total_monto: string; programados: number }>('/retenciones/reporte-reintegros'), enabled: habilitado });
export const useVersionesRetencion = (id: string | null) => useQuery({ queryKey: clavesRetenciones.versiones(id ?? ''), queryFn: () => get<VersionRetencionDTO[]>(`/retenciones/${id}/versiones`), enabled: !!id });
export const generarAlertas = () => post<{ generadas: number }>('/retenciones/alertas');

function useInvalidar() {
  const qc = useQueryClient();
  return () => Promise.all([qc.invalidateQueries({ queryKey: ['retenciones'] }), qc.invalidateQueries({ queryKey: ['clientes'] }), qc.invalidateQueries({ queryKey: ['ventas'] }), qc.invalidateQueries({ queryKey: ['cierres'] }), qc.invalidateQueries({ queryKey: ['notificaciones'] })]);
}
export function useCargarComprobante() {
  const inv = useInvalidar();
  return useMutation({ mutationFn: ({ id, datos }: { id: string; datos: ComprobanteInput }) => post<RetencionFilaDTO>(`/retenciones/${id}/comprobante`, datos), onSuccess: () => void inv() });
}
export function useCorregirComprobante() {
  const inv = useInvalidar();
  return useMutation({ mutationFn: ({ id, datos }: { id: string; datos: CorregirComprobanteInput }) => put<RetencionFilaDTO>(`/retenciones/${id}/comprobante`, datos), onSuccess: () => void inv() });
}
export function useReintegrar() {
  const inv = useInvalidar();
  return useMutation({ mutationFn: ({ id, datos }: { id: string; datos: ReintegroInput }) => post<RetencionFilaDTO>(`/retenciones/${id}/reintegro`, datos), onSuccess: () => void inv() });
}
export function useEjecutarProgramado() {
  const inv = useInvalidar();
  return useMutation({ mutationFn: (id: string) => post<RetencionFilaDTO>(`/retenciones/${id}/ejecutar-programado`), onSuccess: () => void inv() });
}
/** Retención a posteriori: comprobante de una factura que se emitió sin retención. */
export function useRetencionPosterior() {
  const inv = useInvalidar();
  return useMutation({ mutationFn: (datos: RetencionPosteriorInput) => post<RetencionFilaDTO>('/retenciones/posterior', datos), onSuccess: () => void inv() });
}
export function useEnviarARevision() {
  const inv = useInvalidar();
  return useMutation({ mutationFn: ({ id, observaciones }: { id: string; observaciones: string }) => post<RetencionFilaDTO>(`/retenciones/${id}/revisar`, { observaciones }), onSuccess: () => void inv() });
}
export function useMarcarRevisada() {
  const inv = useInvalidar();
  return useMutation({ mutationFn: ({ id, monto, sin_efecto, observaciones }: { id: string; monto?: string; sin_efecto?: boolean; observaciones: string }) => post<RetencionFilaDTO>(`/retenciones/${id}/revisada`, { ...(monto ? { monto } : {}), ...(sin_efecto ? { sin_efecto: true } : {}), observaciones }), onSuccess: () => void inv() });
}
